/** @format */
import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from '~/Store';
import { Pagination } from '~/Api/Pagination';
import { UserProductGroup, UserProductGroupErrorResponse } from '~/Api/User/ProductGroup';
import { Item as ProductGroupItem } from '~/Api/User/ProductGroup/Product';
import config from '~/config';
import { selectCurrency } from '~/Currency';
import { fetchCurrentCustomerDatasourceLicencesIfNeeded } from '~/CustomerDatasourceLicence';
import { handleError } from '~/ErrorHandler';
import { flashSuccessMessage } from '~/FlashMessage';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import {
	fetchMoreItems,
	markProductPackageSizeForSingleUnitChanged,
	pruneProducts,
	removeFromGroup,
	selectItemsByGroupId,
	State as GroupProductState,
	updateProductPackageSizeForSingleUnit,
} from '~/GroupProduct';
import { PricedProduct, Product } from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { fetchPrices, pruneAllPrices, selectProductPrice, State as ProductPriceState } from '~/ProductPrice';
import { pruneAllHistory } from '~/ProductPriceHistory';

import ProductList from './ProductList';

const memoizedCreatePricedProducts = memoizeOne(
	(
		items: ProductGroupItem[],
		state: ProductPriceState,
		currency: string | null,
		priceType: string | null,
		filterCalculatedPrice: boolean,
		groupId: string | null
	) => {
		return items.map(
			(item) =>
				new PricedProduct(
					item,
					item.product,
					selectProductPrice(state, item.product, currency, priceType, filterCalculatedPrice, groupId)
				)
		);
	}
);

interface State {
	groupProduct: GroupProductState;
	productPrice: ProductPriceState;
}

interface OwnProps {
	priceType: string | null;
	filterCalculatedPrice: boolean;
	group: UserProductGroup;
	currency: string | null;
}

interface Props {
	currency: string | null;
	products: ProductGroupItem[];
	pricedProducts: PricedProduct<ProductGroupItem>[];
	hasNextPage: boolean;
	pagination: Pagination | null;
	isFetchingProducts: boolean;
	isFetchingProductPrices: boolean;
}

function mapStateToProps(
	{ groupProduct, productPrice }: State,
	{ priceType, filterCalculatedPrice, group, currency: currencyCode }: OwnProps
): Props {
	const itemsState = selectItemsByGroupId(groupProduct, group.id);

	const currency = currencyCode
		? selectCurrency(group.exchangeRates.availableCurrencies, currencyCode)
			? currencyCode
			: config.product.defaultPriceCurrency
		: null;

	const pricedProducts = itemsState
		? memoizedCreatePricedProducts(itemsState.items, productPrice, currency, priceType, filterCalculatedPrice, group.id)
		: [];

	return {
		currency,
		products: itemsState?.items || [],
		pricedProducts,
		hasNextPage: Boolean(itemsState && itemsState.pagination && itemsState.pagination.hasNextPage()),
		pagination: itemsState?.pagination || null,
		isFetchingProducts: itemsState?.isFetching || false,
		isFetchingProductPrices: productPrice.request !== null,
	};
}

function mapDispatchToProps(
	dispatch: ThunkDispatch<AnyAction>,
	props: OwnProps
): {
	onCurrencyChanged: (a: string | null, b: PricedProduct<ProductGroupItem>[]) => Promise<void>;
	onFetch: (reload?: boolean) => Promise<void>;
	onChangeProductPackageSizeForSingleUnit: (groupId: string, productId: string) => void;
	onCommitProductPackageSizeForSingleUnit: (
		groupId: string,
		productId: string,
		packageSizeForSingleUnit: string | null,
		currency: string | null
	) => void;
	onDeleteItems: (group: UserProductGroup, products: Product[], onSuccess: () => void) => Promise<void>;
	onPrune: () => void;
} {
	return {
		onDeleteItems: async (group: UserProductGroup, products: Product[], onSuccess: () => void) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
		onCurrencyChanged: async (currency, products) => {
			try {
				if (currency !== config.product.defaultPriceCurrency) {
					await dispatch(
						fetchPrices(
							products.map((product) => product.product.id),
							currency,
							props.group.id
						)
					);
				}
				dispatch(flashSuccessMessage('Products currency has been changed'));
			} catch (error) {
				dispatch(handleError(error));
			}
		},
		onFetch: async (reload = false) => {
			if (reload) {
				dispatch(pruneProducts(props.group.id));
			}

			const currency = props.currency
				? selectCurrency(props.group.exchangeRates.availableCurrencies, props.currency)
					? props.currency
					: config.product.defaultPriceCurrency
				: null;

			dispatch(fetchMoreItems(props.group.id))
				// @ts-expect-error strange definition error
				.then((response) => {
					if (response !== null && currency !== config.product.defaultPriceCurrency && response.items.length > 0) {
						return dispatch(
							fetchPrices(
								response.items.map((item) => item.product.id),
								currency,
								props.group.id
							)
						);
					}
					return response;
				})
				.catch((response) => {
					if (!(response instanceof UserProductGroupErrorResponse)) {
						dispatch(handleError(response));
						return undefined;
					}
					response.handleProductGroupNotFound();
					dispatch(handleError(response));
				});

			dispatch(fetchCurrentCustomerDatasourceLicencesIfNeeded());
		},
		onPrune: () => {
			dispatch(pruneProducts(props.group.id));
			dispatch(pruneAllPrices());
			dispatch(pruneAllHistory());
			dispatch(pruneDatasets());
		},
		onChangeProductPackageSizeForSingleUnit: (groupId: string, productId: string) => {
			dispatch(markProductPackageSizeForSingleUnitChanged(groupId, productId));
		},
		onCommitProductPackageSizeForSingleUnit: (
			groupId: string,
			productId: string,
			packageSizeForSingleUnit: string | null,
			currency: string | null
		) => {
			dispatch(
				updateProductPackageSizeForSingleUnit(groupId, productId, packageSizeForSingleUnit, async () => {
					if (currency !== config.product.defaultPriceCurrency) {
						await dispatch(fetchPrices([productId], currency, groupId));
					}
				})
			);
		},
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductList);
